const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/Pickers/pickerDefaults.ts';
import { Theme, useTheme } from '@mui/material/styles';
import { PickersActionBarAction } from '@mui/x-date-pickers';

/**
 * Single source of truth for how every DatePicker / DateTimePicker behaves.
 *
 * Responsive strategy and the action-bar policy live here so that a change to picker behaviour only ever has to be made
 * in one place. Field wrappers (Forms/DatePicker, Forms/DateTimePicker) consume these helpers
 */

/** MUI passes this to the `actionBar` slotProps callback. */
export type PickerVariant = 'desktop' | 'mobile';

export interface PickerActionOptions {
    /** A required field must be committable but never emptied, so Clear is withheld. */
    required?: boolean;
    /**
     * Whether the field supports being cleared to an empty value. Defaults to `!required`. Set to `false` for fields
     * whose `onChange` cannot handle a `null` value.
     */
    clearable?: boolean;
}

/**
 * The one responsive rule for the whole app: render the desktop popover from the `sm` breakpoint (600px) upwards and
 * the mobile modal below it. This is viewport-based (not MUI's default pointer heuristic), so a desktop browser always
 * gets the desktop experience and only real phones get the modal.
 *
 * Prefer the {@link useDesktopModeMediaQuery} hook in function components; use this theme-taking form in class
 * components where hooks are unavailable.
 */
export const desktopModeMediaQuery = (theme: Theme): string => theme.breakpoints.up('sm');

/** Hook form of {@link desktopModeMediaQuery} for function components. */
export const useDesktopModeMediaQuery = (): string => desktopModeMediaQuery(useTheme());

/**
 * Action bar for date-only pickers.
 * - Today is always offered.
 * - Clear is offered only when the field is clearable (optional and able to handle an empty value).
 * - The desktop popover commits on selection (no Accept); the mobile modal commits only via Accept.
 */
export const dateActions = (
    variant: PickerVariant,
    { required = false, clearable = !required }: PickerActionOptions = {}
): PickersActionBarAction[] => {
    const actions: PickersActionBarAction[] = ['today'];
    if (clearable) {
        actions.push('clear');
    }
    if (variant === 'mobile') {
        actions.push('accept');
    }
    return actions;
};

/**
 * Action bar for date-time pickers.
 * - Today (relabelled "Now" via localeText) is always offered.
 * - Clear is offered only when the field is clearable.
 * - The desktop popover has separate date and time steps and does not close on selection, so it keeps Cancel; both
 *   variants commit via Accept.
 */
export const dateTimeActions = (
    variant: PickerVariant,
    { required = false, clearable = !required }: PickerActionOptions = {}
): PickersActionBarAction[] => {
    const actions: PickersActionBarAction[] = ['today'];
    if (clearable) {
        actions.push('clear');
    }
    if (variant === 'desktop') {
        actions.push('cancel');
    }
    actions.push('accept');
    return actions;
};
